import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Row, Col, Card, CardBody, CardText, CardTitle } from "react-bootstrap";
import { NumericFormat as NumberFormat } from "react-number-format";

import { deepEqual } from "../../../utils/object";
import { list as getRates } from "../../../actions/rates";

function Rate({ title, children }) {
  return (
    <Col sm="6" md="3" xl="2" className="mb-3 d-flex">
      <Card className="w-100 h-100">
        <CardBody className="d-flex flex-column">
          <CardTitle>{title}</CardTitle>
          <CardText className="mt-auto">{children}</CardText>
        </CardBody>
      </Card>
    </Col>
  );
}

function Rates(props) {
  const { rates: propRates, onChange, getRates: loadRates } = props;
  const [baseRates, setBaseRates] = useState([]);
  const [rates, setRates] = useState([]);

  const hydratingRef = useRef(true);
  const propRatesRef = useRef(propRates);

  const normalizeRates = useCallback((payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  }, []);

  const byUuidMerge = useCallback((primary = [], secondary = []) => {
    const map = new Map();
    secondary.forEach((r) => map.set(r.uuid, r));
    primary.forEach((r) => map.set(r.uuid, { ...map.get(r.uuid), ...r }));
    return Array.from(map.values());
  }, []);

  useEffect(() => {
    propRatesRef.current = propRates;
  }, [propRates]);

  useEffect(() => {
    let active = true;
    hydratingRef.current = true;

    const loadData = async () => {
      const raw = await loadRates();
      const loaded = normalizeRates(raw);

      if (!active) return;

      setBaseRates(loaded);
      setRates(byUuidMerge(propRatesRef.current ?? [], loaded));

      queueMicrotask(() => {
        if (active) hydratingRef.current = false;
      });
    };

    loadData();

    return () => {
      active = false;
    };
  }, [loadRates, normalizeRates, byUuidMerge]);

  const mergedRates = useMemo(
    () => byUuidMerge(propRates ?? [], baseRates),
    [propRates, baseRates, byUuidMerge]
  );

  useEffect(() => {
    if (deepEqual(mergedRates, rates)) return;

    hydratingRef.current = true;
    setRates(mergedRates);

    queueMicrotask(() => {
      hydratingRef.current = false;
    });
  }, [mergedRates, rates]);

  const title = useCallback((name, style) => {
    return `${name} (${style === "H" ? "Hourly" : style === "C" ? "Contingency" : "Flat"})`;
  }, []);

  const onChangeValue = useCallback(
    (index, floatValue, sourceInfo) => {
      if (sourceInfo?.source && sourceInfo.source !== "event") return;

      const next = rates.map((rate, rateIndex) => (
        rateIndex === index
          ? { ...rate, value: floatValue ?? 0 }
          : { ...rate }
      ));

      if (deepEqual(next, rates)) return;

      setRates(next);

      if (onChange && !hydratingRef.current) {
        onChange(next);
      }
    },
    [onChange, rates]
  );

  return (
    <Row>
      {rates.map((r, i) => (
        <Rate key={r.uuid ?? i} title={title(r.name, r.style)}>
          <NumberFormat
            displayType="input"
            type="text"
            className="text-center form-control mb-2"
            prefix={r.style === "C" ? "" : "$"}
            suffix={r.style === "C" ? "%" : ""}
            thousandSeparator
            allowNegative={false}
            value={r.value ?? 0}
            onValueChange={(e, sourceInfo) => onChangeValue(i, e.floatValue, sourceInfo)}
            removeFormatting
            isAllowed={({ floatValue, value }) =>
              value === "" || (floatValue ?? 0) <= (r.style === "C" ? 100 : 50000000)
            }
          />
        </Rate>
      ))}
    </Row>
  );
}

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps, { getRates })(Rates);
